<template>
  <v-dialog v-model="dialog" persistent max-width="500">
    <v-card variant="flat">
      <v-container>
        <!-- CAMERA SCANNER -->
        <v-row v-if="!physicalScanner">
          <v-col cols="12">
            <qrcode-stream
              v-if="dialog"
              :key="cameraFacing"
              :camera="cameraFacing"
              :paused="paused"
              @decode="decode"
              @init="onInit"
            >
              <template #loading>
                <v-progress-circular indeterminate color="primary" />
              </template>
              <template #error="err">
                <v-alert type="error" variant="tonal">
                  {{ err?.message || $t("Camera access error") }}
                </v-alert>
              </template>
            </qrcode-stream>
          </v-col>
        </v-row>

        <!-- PHYSICAL SCANNER -->
        <v-row v-if="physicalScanner" justify="center" class="mt-2 mb-2">
          <div
            v-if="found === null && !physicalScannerError"
            class="ma-3 text-blue text-subtitle-1"
          >
            {{ $t("parts.inventory.qr_scanner.physical_scanner") }}
          </div>
          <div
            v-if="physicalScannerError"
            class="ma-3 text-red text-subtitle-1"
          >
            {{ $t("parts.inventory.qr_scanner.physical_scanner_error") }}
          </div>
        </v-row>

        <!-- LOADER -->
        <v-row justify="center">
          <v-progress-circular v-if="loading" indeterminate color="primary" />
        </v-row>

        <!-- ALERT -->
        <v-row>
          <v-col cols="12">
            <v-alert
              v-model="alert.value"
              :type="alert.type"
              variant="outlined"
              density="comfortable"
            >
              {{ alert.message }}
            </v-alert>
          </v-col>
        </v-row>

        <!-- ACTION BUTTONS WHEN FOUND -->
        <v-row
          v-if="found === true && type === 'part'"
          class="mt-2"
          justify="center"
        >
          <v-col
            v-if="mode === 'single' || mode === 'multiple'"
            cols="12"
            sm="6"
          >
            <v-btn
              variant="outlined"
              color="grey-darken-2"
              block
              @click="scanAnotherPart"
            >
              {{ $t("parts.inventory.qr_scanner.scan_another_part") }}
              <v-icon end>mdi-autorenew</v-icon>
            </v-btn>
          </v-col>

          <v-col v-if="mode === 'single'" cols="12" sm="6">
            <v-btn
              variant="outlined"
              color="grey-darken-2"
              block
              :disabled="partType === 'temporary'"
              @click="details"
            >
              {{ $t("parts.inventory.qr_scanner.view_details") }}
              <v-icon end>mdi-view-list</v-icon>
            </v-btn>
          </v-col>

          <v-col cols="12" sm="6">
            <v-btn
              variant="outlined"
              color="grey-darken-2"
              block
              @click="actions"
            >
              {{ $t("parts.inventory.qr_scanner.actions") }}
              <v-icon end>mdi-compare-arrows</v-icon>
            </v-btn>
          </v-col>

          <v-col cols="12" sm="6">
            <v-btn
              variant="outlined"
              color="grey-darken-2"
              block
              @click="changeRepo"
            >
              {{ $t("parts.inventory.qr_scanner.change_location") }}
              <v-icon end>mdi-door-open</v-icon>
            </v-btn>
          </v-col>

          <v-col
            v-if="
              mode === 'single' &&
              ['LekoAdmin', 'InstitutionAdmin', 'SuperAdmin'].includes(
                $store.getters.role,
              )
            "
            cols="12"
            sm="6"
          >
            <v-btn
              variant="outlined"
              color="grey-darken-2"
              block
              :disabled="partType === 'temporary'"
              @click="edit"
            >
              {{ $t("parts.inventory.qr_scanner.edit_part") }}
              <v-icon end>mdi-pencil</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <!-- MULTIPLE SCANNED CODES -->
        <v-row v-if="mode === 'multiple' && parts.length > 0" class="mt-2">
          <v-chip
            v-for="p in parts"
            :key="p.part._id"
            closable
            @click:close="removeId(p.part)"
          >
            {{
              p.part?.old_id ? p.part.old_id.slice(-6) : p.part._id.slice(-6)
            }}
          </v-chip>
        </v-row>

        <!-- REPOSITORY LOCATION SELECT -->
        <v-row
          v-if="found === true && type === 'repo'"
          class="mt-3"
          align="center"
          justify="center"
        >
          <v-col cols="6">
            <v-select
              v-model="repository"
              :items="repositories"
              :label="$t('parts.inventory.qr_scanner.select_repository')"
              color="red-darken-1"
              item-title="name"
              return-object
            />
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="room"
              :items="repository.rooms"
              :label="$t('parts.inventory.qr_scanner.select_room')"
              color="red-darken-1"
              item-title="name"
              return-object
            />
          </v-col>
          <v-col cols="4">
            <v-select
              v-model="location"
              :items="room.locations"
              :label="$t('parts.inventory.qr_scanner.select_location')"
              color="red-darken-1"
              item-title="code"
              return-object
            />
          </v-col>
          <v-col cols="4">
            <v-select
              v-model="shelf.x"
              :items="xPositions"
              :label="$t('parts.inventory.qr_scanner.select_x')"
              color="red-darken-1"
            />
          </v-col>
          <v-col cols="4">
            <v-select
              v-model="shelf.y"
              :items="yPositions"
              :label="$t('parts.inventory.qr_scanner.select_y')"
              color="red-darken-1"
            />
          </v-col>
          <v-col cols="12" class="text-center">
            <v-btn
              :loading="loading2"
              variant="outlined"
              color="primary"
              @click="changeLocation"
            >
              {{ $t("parts.inventory.qr_scanner.change_location") }}
            </v-btn>
          </v-col>
        </v-row>

        <!-- BOTTOM ACTION BUTTONS -->
        <v-row class="mt-4" align="center" justify="end">
          <v-btn icon variant="text" @click="reScan">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
          <v-btn icon variant="text" @click="switchScanner">
            <v-icon>mdi-qrcode-scan</v-icon>
          </v-btn>
          <v-btn icon variant="text" @click="switchCamera">
            <v-icon>mdi-camera-switch</v-icon>
          </v-btn>
          <v-btn variant="outlined" color="error" @click="close">
            {{ $t("parts.inventory.qr_scanner.close") }}
          </v-btn>
        </v-row>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
import getRequest from "../../api/getRequest";
import postRequest from "../../api/postRequest";
import putRequest from "../../api/putRequest";
import { QrcodeStream } from "vue3-qrcode-reader";
export default {
  components: {
    QrcodeStream,
  },
  data: () => ({
    dialog: false,
    /** Prefer rear on mobile; 'auto' falls back to front/webcam on desktop. */
    cameraFacing: "auto",
    paused: false,
    loading: false,
    loading2: false,
    alert: {
      value: false,
      type: "success",
      message: "",
    },
    part: {},
    partType: "",
    repositories: [],
    repository: {},
    room: {},
    location: {},
    shelf: {},
    xPositions: [],
    yPositions: [],
    found: null,
    decodedString: null,
    type: "part",
    hideLabel: true,
    physicalScanner: false,
    physicalScannerError: false,
    buffer: "",
    mode: "single",
    parts: [],
  }),
  watch: {
    location: {
      handler(value) {
        this.generateCoordinates(value.position_x, value.position_y);
      },
      deep: true,
    },
  },
  methods: {
    async onInit(promise) {
      try {
        await promise;
        this.alert.value = false;
      } catch (err) {
        if (err.name === "NotAllowedError") {
          this.alert = {
            value: true,
            type: "error",
            message: this.$t(
              "Camera access denied. Using physical scanner mode.",
            ),
          };
          this.switchScanner();
        } else if (
          (err.name === "OverconstrainedError" ||
            err.name === "NotFoundError") &&
          this.cameraFacing !== "auto"
        ) {
          this.cameraFacing = "auto";
        } else {
          this.alert = {
            value: true,
            type: "error",
            message: this.$t("Cannot access camera."),
          };
        }
      }
    },
    switchCamera() {
      this.physicalScanner = false;
      document.removeEventListener("keydown", this.listenToEvent);
      this.cameraFacing = this.cameraFacing === "front" ? "rear" : "front";
    },
    switchScanner() {
      this.physicalScanner = true;
      document.addEventListener("keydown", this.listenToEvent);
    },
    listenToEvent(e) {
      if (e.key !== "Enter") {
        this.buffer += e.key;
      } else if (this.buffer.length > 24) {
        this.physicalScannerError = true;
        this.buffer = "";
      } else {
        this.decode(this.buffer);
        this.buffer = "";
      }
    },
    decode(value) {
      if (value !== null && value.length > 0) {
        this.loading = true;
        this.found = null;
        this.hideLabel = true;
        this.decodedString = value;
        this.paused = true;
        this.alert.value = false;
        if (this.type === "part") {
          getRequest(`/part/inventory/single/${value}/`)
            .then((response) => {
              this.part = response.part;
              this.partType = response.type;
              const found = this.parts.find(
                (p) => p.part._id.toString() === this.part._id.toString(),
              );
              if (!found) {
                this.parts.push({ part: this.part, type: this.partType });
              }
              this.found = true;
              this.loading = false;

              this.alert = {
                value: true,
                type: "success",
                message: `${this.$t("parts.inventory.qr_scanner.found_message_1")} ${this.$t("parts.inventory.qr_scanner.part")} ${this.$t("parts.inventory.qr_scanner.message_2")}: ${this.decodedString}`,
              };
            })
            .catch((error) => {
              this.part = {};
              this.found = false;
              this.loading = false;

              this.alert = {
                value: true,
                type: "error",
                message: `${this.$t("parts.inventory.qr_scanner.not_found_message_1")} ${this.$t("parts.inventory.qr_scanner.part")} ${this.$t("parts.inventory.qr_scanner.message_2")}: ${this.decodedString}`,
              };
            });
        } else if (this.type === "repo") {
          getRequest("/repository/shelf/", `id=${value}`)
            .then((response) => {
              const repository = response.repository; // eslint-disable-line prefer-destructuring
              this.repository = repository;
              const room = repository.rooms[response.roomIndex];
              this.room = room;
              const location = room.locations[response.locationIndex];
              this.location = location;
              this.shelf = response.shelf;
              this.found = true;
              this.loading = false;

              this.alert = {
                value: true,
                type: "success",
                message: `${this.$t("parts.inventory.qr_scanner.found_message_1")} ${this.$t("parts.inventory.qr_scanner.repository")} ${this.$t("parts.inventory.qr_scanner.message_2")}: ${this.decodedString}`,
              };
            })
            .catch((error) => {
              this.clearRepo();
              this.found = false;
              this.loading = false;

              this.alert = {
                value: true,
                type: "error",
                message: `${this.$t("parts.inventory.qr_scanner.not_found_message_1")} ${this.$t("parts.inventory.qr_scanner.repository")} ${this.$t("parts.inventory.qr_scanner.message_2")}: ${this.decodedString}`,
              };
            });
        }
      }
    },
    reScan() {
      this.found = null;
      this.hideLabel = false;
      this.loading = false;
      this.paused = false;
      this.decodedString = null;
      this.physicalScannerError = false;
      if (this.type === "part") {
        this.part = {};
        this.clearRepo();
        this.alert = {
          value: true,
          type: "info",
          message: this.$t("parts.inventory.qr_scanner.scan_part_message"),
        };
      } else if (this.type === "repo") {
        this.clearRepo();
        this.alert = {
          value: true,
          type: "info",
          message: this.$t("parts.inventory.qr_scanner.scan_shelf_message"),
        };
      }
    },
    scanAnotherPart() {
      this.mode = "multiple";
      this.reScan();
    },
    removeId(part) {
      const index = this.parts.findIndex(
        (p) => p._id.toString() === part._id.toString(),
      );
      if (index > -1) this.parts.splice(index, 1);
    },
    details() {
      this.$emit("details", this.part);
      this.close();
    },
    actions() {
      this.$emit("actions", { part: this.part, parts: this.parts });
      this.close();
    },
    changeRepo() {
      this.type = "repo";
      this.found = null;
      this.hideLabel = false;
      this.paused = false;
      this.decodedString = null;
      this.loading = false;
      this.getRepositories();

      this.alert = {
        value: true,
        type: "info",
        message: this.$t("parts.inventory.qr_scanner.scan_shelf_message"),
      };
    },
    clearRepo() {
      this.repository = {};
      this.room = {};
      this.location = {};
      this.shelf = {};
      this.xPositions = [];
      this.yPositions = [];
    },
    getRepositories() {
      getRequest(
        "/repository/",
        `role=${this.$store.getters.role}&institution_id=${
          this.$store.getters.institutionId
        }`,
      )
        .then((response) => {
          this.repositories = response.repositories;
        })
        .catch(() => {});
    },
    generateCoordinates(x, y) {
      const xArr = [];
      const yArr = [];
      for (let xc = 1; xc <= x; xc += 1) {
        xArr.push(xc);
      }
      for (let yc = 1; yc <= y; yc += 1) {
        yArr.push(yc);
      }
      this.xPositions = xArr;
      this.yPositions = yArr;
    },
    changeLocation() {
      this.loading2 = true;
      const parts = [];
      const tempParts = [];
      this.parts.forEach((part) => {
        if (part.type === "inventory") parts.push(part.part);
        if (part.type === "temporary") tempParts.push(part.part);
      });

      const promises = [];
      // Change inventory parts' locations.
      if (parts.length) {
        promises.push(
          putRequest("/part/location", "", {
            parts_ids: parts.map((p) => p._id),
            repository_id: this.repository._id,
            location_id: this.location._id,
            room_id: this.room._id,
            shelf_id: this.shelf._id,
          }),
        );
      }

      // Change temporary parts' locations.
      if (tempParts.length) {
        promises.push(
          postRequest.object("/temporary-part/insert/location", {
            parts_ids: tempParts.map((p) => p._id),
            repository_id: this.repository._id,
            location_id: this.location._id,
            room_id: this.room._id,
            shelf_id: this.shelf._id,
          }),
        );
      }

      Promise.all(promises)
        .then((response) => {
          if (response) {
            this.$emit("changed-location");
            this.close();
          }
        })
        .catch(() => {
          this.close();
        });
    },
    edit() {
      this.$emit("edit", this.part);
      this.close();
    },
    open() {
      this.cameraFacing = "auto";
      this.dialog = true;
      this.alert = {
        value: true,
        type: "info",
        message: this.$t("parts.inventory.qr_scanner.scan_part_message"),
      };
    },
    close() {
      this.dialog = false;
      this.cameraFacing = "auto";
      this.paused = false;
      this.loading = false;
      this.loading2 = false;
      this.part = {};
      this.parts = [];
      this.partType = "";
      this.clearRepo();
      this.found = null;
      this.decodedString = null;
      this.type = "part";
      this.physicalScanner = false;
      this.physicalScannerError = false;
      this.alert = {
        value: false,
        type: "success",
        message: "",
      };
      this.mode = "single";
      this.parts = [];
      document.removeEventListener("keydown", this.listenToEvent);
    },
  },
};
</script>
